import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import FilterBar from "@/components/common/FilterBar";
import EmptyState from "@/components/common/EmptyState";
import StatusBadge from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Plus, FileCheck2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSortableList, SortHeader } from "@/components/common/useSortableList";
import { toast } from "sonner";

export default function BillsPage({ docType = "bill", title = "Bills", kicker = "PURCHASES", subtitle = "Vendor bills and their lifecycle." }) {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [vendors, setVendors] = useState([]);
    const [openForm, setOpenForm] = useState(false);
    const [form, setForm] = useState({ vendor_id: "", date: new Date().toISOString().slice(0, 10), due_date: "", description: "", amount: 0, tax_rate: 18 });
    const [loading, setLoading] = useState(true);

    const { sorted, sortKey, sortDir, onSort } = useSortableList(items, {
        storageKey: `bills-${docType}`,
        initial: { key: "date", dir: "desc" },
        search,
        searchKeys: ["number", "vendor_name"],
    });

    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/bills", { params: { type: docType } });
        setItems(data); setLoading(false);
    };
    useEffect(() => { load(); api.get("/vendors").then(({ data }) => setVendors(data)); }, [docType]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.vendor_id || !form.amount) return toast.error("Vendor and amount required");
        await api.post("/bills", {
            type: docType,
            vendor_id: form.vendor_id,
            date: form.date,
            due_date: form.due_date || form.date,
            currency: "INR",
            lines: [{ item_name: form.description || "Expense", quantity: 1, rate: Number(form.amount), tax_rate: Number(form.tax_rate), tax_type: "GST", discount_percent: 0 }],
        });
        toast.success("Bill saved");
        setOpenForm(false);
        await load();
    };

    return (
        <>
            <PageHeader kicker={kicker} title={title} subtitle={subtitle}
                actions={<Button onClick={() => setOpenForm(true)} data-testid="bill-new"><Plus className="w-4 h-4 mr-1.5" />New {docType.replace("_", " ")}</Button>} />
            <PageBody>
                <FilterBar search={search} onSearch={setSearch} />
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {loading ? <div className="p-8 text-muted-foreground text-center">Loading…</div> :
                        sorted.length === 0 ? <EmptyState icon={FileCheck2} title="No bills" description="Record vendor bills to track payables and GST input." /> :
                            <table className="fin-table">
                                <thead><tr>
                                    <SortHeader label="Number" sortKey="number" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Vendor" sortKey="vendor_name" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Date" sortKey="date" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Due" sortKey="due_date" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Total" sortKey="total" currentKey={sortKey} dir={sortDir} onSort={onSort} align="right" />
                                    <SortHeader label="Balance" sortKey="balance_due" currentKey={sortKey} dir={sortDir} onSort={onSort} align="right" />
                                    <SortHeader label="Status" sortKey="status" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                </tr></thead>
                                <tbody>
                                    {sorted.map((b) => (
                                        <tr key={b.id} data-testid={`bill-row-${b.id}`}>
                                            <td className="font-mono text-[12.5px] font-medium">{b.number}</td>
                                            <td>{b.vendor_name}</td>
                                            <td className="text-[12px] text-muted-foreground">{fmtDate(b.date)}</td>
                                            <td className="text-[12px] text-muted-foreground">{fmtDate(b.due_date)}</td>
                                            <td className="num">{fmtMoney(b.total, b.currency)}</td>
                                            <td className="num">{fmtMoney(b.balance_due, b.currency)}</td>
                                            <td><StatusBadge status={b.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>}
                </div>
            </PageBody>
            <Sheet open={openForm} onOpenChange={setOpenForm}>
                <SheetContent side="right" className="w-full sm:max-w-[460px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4"><SheetTitle className="text-xl font-semibold tracking-tight">New {docType.replace("_", " ")}</SheetTitle></SheetHeader>
                    <form className="flex-1 overflow-y-auto p-6 space-y-4" onSubmit={submit}>
                        <div>
                            <Label>Vendor</Label>
                            <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="bill-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                                <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" /></div>
                        </div>
                        <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Office rent - November" className="mt-1.5" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Amount (pre-tax)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="bill-amount" /></div>
                            <div><Label>GST %</Label><Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} className="mt-1.5" /></div>
                        </div>
                        <Button type="submit" className="w-full" data-testid="bill-save">Save bill</Button>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
}
